import filters from '@/filters/index';
import {
  fillRichSlots,
  readRichSlotFormat,
  richSlotMarker,
  withoutSlotMarker,
} from '@/utils/rich_template_slot';
import type { RichSlotFormat } from '@/utils/rich_template_slot';
import { toWallClockDate, toWallClockTimeInputValue } from '@/utils/wall_clock_date';

/**
 * Shiurei Torah templates: the placeholders a template can use, how a Shiurei
 * Torah is drawn through one, and the sample values its preview is drawn with.
 *
 * renderShiureiTorahTemplate is the one renderer — the display
 * (Display.vue → renderShiureiTorahContent), the live preview on the Register /
 * Edit / Duplicate pages, the import preview and the template previews all go
 * through it, so a Shiurei Torah looks the same in each.
 */

/**
 * The rich-text fields of a Shiurei Torah a template places: the content, and
 * three more a template can lay out on their own — a rabbi's name, a place, a
 * time — each styled where the template puts it.
 */
export const SHIUREI_TORAH_CONTENT_FIELDS = ['content', 'content1', 'content2', 'content3'] as const;
export type ShiureiTorahContentField = (typeof SHIUREI_TORAH_CONTENT_FIELDS)[number];

/** The three optional ones, as the forms list them. */
export const SHIUREI_TORAH_EXTRA_CONTENT_FIELDS: Array<{ key: Exclude<ShiureiTorahContentField, 'content'>; label: string }> = [
  { key: 'content1', label: 'Content 1' },
  { key: 'content2', label: 'Content 2' },
  { key: 'content3', label: 'Content 3' },
];

// For the editor's Variable menu (Rich_Text_Editor `variables`), in the same
// shape and wording as the donation templates' list.
export const SHIUREI_TORAH_TEMPLATE_VARIABLES = [
  { value: 'title', label: "Insert 'title'" },
  { value: 'content', label: "Insert 'content'" },
  { value: 'content1', label: "Insert 'content1'" },
  { value: 'content2', label: "Insert 'content2'" },
  { value: 'content3', label: "Insert 'content3'" },
  { value: 'displayStartDate', label: "Insert 'displayStartDate'" },
  { value: 'displayEndDate', label: "Insert 'displayEndDate'" },
  { value: 'startTime', label: "Insert 'startTime'" },
  { value: 'endTime', label: "Insert 'endTime'" },
];

// Every placeholder, matched in one pass: a Shiurei Torah whose own text holds
// "{startTime}" is left as written.
const TOKEN_PATTERN =
  /{(title|content|content1|content2|content3|displayStartDate|displayEndDate|startTime|endTime)}/g;

const isContentField = (token: string): token is ShiureiTorahContentField =>
  (SHIUREI_TORAH_CONTENT_FIELDS as ReadonlyArray<string>).includes(token);

/** What goes into a template's placeholders, already written out for the screen. */
export interface ShiureiTorahTemplateValues {
  title: string;
  /** Rich text from the editor — as are content1–content3. */
  content: string;
  content1: string;
  content2: string;
  content3: string;
  displayStartDate: string;
  displayEndDate: string;
  startTime: string;
  endTime: string;
}

/** Whether rich-text HTML holds any visible text. */
export const shiureiTorahTemplateHasText = (html: string | null | undefined): boolean =>
  String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;

// Content without any text: an image, a video, a rule.
const MEDIA_CONTENT = /<(img|video|iframe|audio|embed|object|svg|hr)\b/i;

/**
 * Whether an optional content field (content1–content3) holds anything: text,
 * or media on its own — a speaker's photo is content too, and is kept.
 */
export const shiureiTorahContentHasSubstance = (html: string | null | undefined): boolean =>
  shiureiTorahTemplateHasText(html) || MEDIA_CONTENT.test(String(html || ''));

/** Whether a template shows the given content field ({content} unless named). */
export const shiureiTorahTemplateHasContent = (
  template: string | null | undefined,
  field: ShiureiTorahContentField = 'content'
): boolean => String(template || '').includes(`{${field}}`);

/**
 * A Shiurei Torah drawn through its template. Every placeholder is swapped for
 * its value in one pass; the content fields go in last, each in the formatting
 * the template gives its own placeholder (rich_template_slot). No template:
 * the content exactly as written.
 */
export const renderShiureiTorahTemplate = (
  template: string | null | undefined,
  values: ShiureiTorahTemplateValues
): string => {
  const source = withoutSlotMarker(template);
  if (!source) return values.content || '';
  const withSlots = source.replace(TOKEN_PATTERN, (_match, token: keyof ShiureiTorahTemplateValues) =>
    isContentField(token) ? richSlotMarker(token) : withoutSlotMarker(values[token])
  );
  return fillRichSlots(withSlots, {
    content: values.content || '',
    content1: values.content1 || '',
    content2: values.content2 || '',
    content3: values.content3 || '',
  });
};

/**
 * The formatting a template gives a content field's placeholder ({content}
 * unless named), for the editor to write that field in (Rich_Text_Editor
 * `slotFormat`). null without a template, or when it has no such placeholder.
 */
export const readShiureiTorahContentSlot = (
  template: string | null | undefined,
  field: ShiureiTorahContentField = 'content'
): RichSlotFormat | null => {
  const source = withoutSlotMarker(template);
  if (!shiureiTorahTemplateHasContent(source, field)) return null;
  return readRichSlotFormat(
    source.replace(TOKEN_PATTERN, (match, token) => (token === field ? richSlotMarker(field) : match)),
    field
  );
};

/* --------------------------- values, as displayed --------------------------- */

// Display.vue's multilineToHtml: a title typed over two lines keeps them.
const multilineToHtml = (value: unknown): string =>
  value == null ? '' : String(value).replace(/\r\n|\r|\n/g, '<br>');

// Display.vue's formatScheduleDateOnly: the schedule's own wall-clock day.
const formatDate = (value: unknown): string => {
  const date = toWallClockDate(value);
  return date ? filters.getDateOnly(date as unknown as string) : '-';
};

// Display.vue's formatScheduleTimeOnly: the schedule's own wall-clock "HH:mm"
// as a 12-hour clock, so a stored 20:00 reads "8:00 PM".
const formatTime = (value: unknown): string => {
  const parts = toWallClockTimeInputValue(value).match(/^(\d{1,2}):(\d{2})$/);
  if (!parts) return '-';
  const hours = Number(parts[1]);
  return `${hours % 12 || 12}:${parts[2]} ${hours >= 12 ? 'PM' : 'AM'}`;
};

/**
 * The values the display gives a Shiurei Torah's placeholders, for a preview
 * of it: title, the content fields and the start and end of its schedule.
 */
export const shiureiTorahTemplateValues = (record: {
  title?: unknown;
  content?: unknown;
  content1?: unknown;
  content2?: unknown;
  content3?: unknown;
  displayStartDate?: unknown;
  displayEndDate?: unknown;
}): ShiureiTorahTemplateValues => ({
  title: multilineToHtml(record.title),
  content: String(record.content || ''),
  content1: String(record.content1 || ''),
  content2: String(record.content2 || ''),
  content3: String(record.content3 || ''),
  displayStartDate: formatDate(record.displayStartDate),
  displayEndDate: formatDate(record.displayEndDate),
  startTime: formatTime(record.displayStartDate),
  endTime: formatTime(record.displayEndDate),
});

/* --------------------------------- preview --------------------------------- */

// Stand-in values for the template editor's preview only; nothing here is
// persisted. The content fields are paragraphs, as the editor writes them, so
// the preview shows the template's formatting reaching each.
const PREVIEW_SAMPLE: ShiureiTorahTemplateValues = {
  title: 'Daf Yomi Shiur',
  content: '<p>Bava Basra, daf 12</p>',
  content1: '<p>Rabbi Cohen</p>',
  content2: '<p>Beis Medrash, upstairs</p>',
  content3: '<p>Men and women welcome</p>',
  displayStartDate: 'Mon, Jan 5, 2026',
  displayEndDate: 'Fri, Jan 9, 2026',
  startTime: '8:00 PM',
  endTime: '9:00 PM',
};

/** The template filled with sample values, the way the display fills it. */
export const renderShiureiTorahTemplatePreview = (html: string | null | undefined): string => {
  const source = String(html || '');
  if (!shiureiTorahTemplateHasText(source)) {
    return '<p class="stt-preview-empty">Nothing to preview yet.</p>';
  }
  return renderShiureiTorahTemplate(source, PREVIEW_SAMPLE);
};
